import React, { useEffect, useMemo, useState } from 'react';
import { MessageCircle, RefreshCw } from 'lucide-react';
import { useDashboardData } from '../DashboardShell';
import { useLocale } from '../../context/LocaleContext';

function filterByMode(list, mode) {
  if (!Array.isArray(list)) return [];
  if (mode === 'mentions') return list.filter((c) => c.statusTag);
  if (mode === 'unattended') return list.filter((c) => c.assigneeType === 'unassigned');
  return list;
}

function filterByTab(list, tab) {
  if (tab === 'mine') return list.filter((c) => c.assigneeType === 'mine');
  if (tab === 'unassigned') return list.filter((c) => c.assigneeType === 'unassigned');
  return list;
}

/**
 * Three-column layout: sidebar is global; this is list + detail only.
 * @param {'default' | 'mentions' | 'unattended'} mode
 */
export default function ConversationsLayout({ mode = 'default' }) {
  const { data, reload } = useDashboardData();
  const { t } = useLocale();
  const [tab, setTab] = useState('mine');
  const [selectedId, setSelectedId] = useState(null);

  const fullList = useMemo(() => data?.conversations?.list || [], [data?.conversations?.list]);
  const messagesById = data?.conversations?.messagesByConversationId || {};

  const scoped = useMemo(() => filterByMode(fullList, mode), [fullList, mode]);

  const rows = useMemo(() => {
    if (mode !== 'default') return scoped;
    return filterByTab(scoped, tab);
  }, [mode, scoped, tab]);

  const counts = useMemo(() => {
    const base = mode === 'default' ? scoped : fullList;
    return {
      mine: base.filter((c) => c.assigneeType === 'mine').length,
      unassigned: base.filter((c) => c.assigneeType === 'unassigned').length,
      all: base.length,
    };
  }, [fullList, mode, scoped]);

  useEffect(() => {
    if (mode === 'unattended') setTab('unassigned');
  }, [mode]);

  useEffect(() => {
    setSelectedId(null);
  }, [mode, tab]);

  const title =
    mode === 'mentions'
      ? t('conversations.mentions')
      : mode === 'unattended'
        ? t('conversations.unattended')
        : t('nav.allConversations');

  const messages = selectedId != null ? messagesById[selectedId] || [] : [];
  const selectedRow = rows.find((r) => r.id === selectedId);

  return (
    <div className="custio-conv-layout">
      <div className="custio-conv-list">
        <div className="custio-conv-list__head">
          <h2 className="custio-conv-list__title">
            {title}
            <button
              type="button"
              className="custio-compose-btn"
              style={{ width: 32, height: 32 }}
              aria-label={t('common.refresh')}
              onClick={() => reload()}
            >
              <RefreshCw size={16} />
            </button>
          </h2>
          {mode === 'default' && (
            <div className="custio-conv-tabs">
              <button
                type="button"
                className={`custio-conv-tab${tab === 'mine' ? ' custio-conv-tab--active' : ''}`}
                onClick={() => setTab('mine')}
              >
                {t('conversations.mine')} ({counts.mine})
              </button>
              <button
                type="button"
                className={`custio-conv-tab${tab === 'unassigned' ? ' custio-conv-tab--active' : ''}`}
                onClick={() => setTab('unassigned')}
              >
                {t('conversations.unassigned')} ({counts.unassigned})
              </button>
              <button
                type="button"
                className={`custio-conv-tab${tab === 'all' ? ' custio-conv-tab--active' : ''}`}
                onClick={() => setTab('all')}
              >
                {t('conversations.all')} ({counts.all})
              </button>
            </div>
          )}
        </div>
        <div className="custio-conv-list__body">
          {rows.length === 0 ? (
            <p style={{ padding: '24px 16px', color: '#9ca3af', fontSize: 14, margin: 0 }}>
              {t('conversations.empty')}
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={row.id}
                role="button"
                tabIndex={0}
                className={`custio-conv-row${selectedId === row.id ? ' custio-conv-row--active' : ''}`}
                onClick={() => setSelectedId(row.id)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedId(row.id)}
              >
                <div className="custio-conv-row__avatar">{row.avatarInitials || '?'}</div>
                <div className="custio-conv-row__main">
                  <div className="custio-conv-row__name">{row.displayName}</div>
                  <div className="custio-conv-row__preview">{row.lastPreview}</div>
                </div>
                <div className="custio-conv-row__meta">{row.metaLabel}</div>
              </div>
            ))
          )}
        </div>
        <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', padding: '12px 8px' }}>
          {rows.length === 1
            ? t('conversations.inView', { count: rows.length })
            : t('conversations.inViewPlural', { count: rows.length })}
        </p>
      </div>

      <div className="custio-conv-detail">
        {selectedId && selectedRow ? (
          <>
            <div className="custio-conv-detail__icon">
              <MessageCircle size={48} strokeWidth={1.25} />
            </div>
            <p className="custio-conv-detail__title">{selectedRow.displayName}</p>
            <p className="custio-conv-detail__shortcuts" style={{ marginBottom: 16 }}>
              {selectedRow.channelName}
              {selectedRow.statusTag ? ` · ${selectedRow.statusTag}` : ''}
            </p>
            <div
              style={{
                width: '100%',
                maxWidth: 480,
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {messages.map((m) => (
                <div
                  key={m.id}
                  style={{
                    alignSelf: m.sender === 'agent' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    padding: '10px 14px',
                    borderRadius: 12,
                    background: m.sender === 'agent' ? 'rgba(99,102,241,0.25)' : 'rgba(31,34,41,0.9)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    fontSize: 14,
                    color: '#e5e7eb',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>
                    {m.sender === 'agent' ? t('conversations.roleAgent') : t('conversations.roleContact')}
                  </div>
                  {m.body}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="custio-conv-detail__icon">
              <MessageCircle size={48} strokeWidth={1.25} />
            </div>
            <p className="custio-conv-detail__title">{t('conversations.selectPrompt')}</p>
            <div className="custio-conv-detail__shortcuts">
              <div>
                <kbd>⌘</kbd> {t('conversations.shortcutCmdK')}
              </div>
              <div>
                <kbd>⌘</kbd> {t('conversations.shortcutCmdSlash')}
              </div>
            </div>
          </>
        )}
      </div>

      <button type="button" className="custio-fab" aria-label={t('common.chat')}>
        <MessageCircle size={22} strokeWidth={1.75} />
      </button>
    </div>
  );
}
